/**
 * RecentlyViewedTicketsRail - right-rail card listing the 10 tickets
 * the current agent has most recently opened.
 *
 * Replaces the earlier "Related tickets" rail (semantic neighbors)
 * because in practice those neighbors did not feel related and the
 * rail was easier to mistake for an actual signal than to use. The
 * recently-viewed list is what inbox apps usually surface here.
 *
 * Each row shows: ticket_number, subject, company (if known), status.
 * The current ticket is filtered out client-side - it's redundant to
 * tell the agent "you are currently viewing the ticket you are
 * currently viewing".
 *
 * Hidden on small viewports (lg:block hidden) so the detail page stays
 * tight on phones - the rail is a context aid, not the primary content.
 */
import { Link } from "react-router-dom";

import { Card, Spinner } from "@/components/ui";
import { useRecentlyViewedTickets } from "@/hooks/useRecentlyViewedTickets";

import { StatusBadge } from "./StatusBadge";

interface RecentlyViewedTicketsRailProps {
  /** The ticket currently being viewed - filtered out of the rail. */
  currentTicketNumber: number;
  /** How many entries to fetch. Default 11 because we filter the current one. */
  limit?: number;
}

export function RecentlyViewedTicketsRail({
  currentTicketNumber,
  limit = 11,
}: RecentlyViewedTicketsRailProps): JSX.Element {
  const recent = useRecentlyViewedTickets({ limit });
  // Filter the currently-displayed ticket and cap at 10. Backend
  // ordering is already viewed_at DESC.
  const rows = (recent.data ?? [])
    .filter((r) => r.ticket_number !== currentTicketNumber)
    .slice(0, 10);

  return (
    <div className="hidden lg:block" data-testid="recently-viewed-rail">
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <i
            className="fa-duotone fa-thin fa-clock-rotate-left text-base text-neutral-500"
            aria-hidden="true"
          />
          <h2 className="text-sm font-semibold text-neutral-700">
            Recently viewed tickets
          </h2>
        </div>

        {recent.isLoading && (
          <div className="flex items-center justify-center py-6">
            <Spinner />
          </div>
        )}

        {!recent.isLoading && rows.length === 0 && (
          <p className="text-xs text-neutral-500">
            No other recent tickets yet.
          </p>
        )}

        {!recent.isLoading && rows.length > 0 && (
          <ul className="space-y-3">
            {rows.map((row) => (
              <li key={row.ticket_id}>
                <Link
                  to={`/tickets/${row.public_code.toLowerCase()}`}
                  className="block rounded-md border border-neutral-200 bg-white p-2 transition-colors hover:border-primary-300 hover:bg-primary-50"
                  data-testid="recently-viewed-row"
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-neutral-500">
                      #{row.public_code}
                    </span>
                    <StatusBadge status={row.status} />
                  </div>
                  <div className="line-clamp-2 text-sm text-neutral-800">
                    {row.subject}
                  </div>
                  {row.company_name && (
                    <div className="mt-1 truncate text-xs text-neutral-500">
                      {row.company_name}
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export default RecentlyViewedTicketsRail;
